import React from "react";
import styled from "styled-components";

// Shared empty state for the educator Groups page. Used by both the
// CCSS_DIFFERENTIATION branch (ClassroomAssignmentView) and the lesson-flow
// branch (GroupCodeView / LessonGroupsList). Figma node 3805-25947
// ("Groups - No Lesson"). The primary button is wired by each view to its own
// existing assign action; the secondary button opens the Create Quick Group flow.

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 32px 64px;
  text-align: center;
`;

const Message = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Heading = styled.h2`
  color: #000;
  font-family: "ABC Marfa", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
  max-width: 504px;
`;

const BulletList = styled.ul`
  color: #000;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
  max-width: 504px;
  padding-left: 24px;
  text-align: left;

  li + li {
    margin-top: 4px;
  }
  strong {
    font-weight: 700;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
`;

const BaseButton = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 700;
  gap: 4px;
  height: 48px;
  justify-content: center;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px 0 8px;

  &:disabled {
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: #605dec;
  border: none;
  color: #faf7f2;

  &:disabled {
    background: #d5d3ce;
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: #fff;
  border: 1px solid #605dec;
  color: #605dec;

  &:hover:not(:disabled) {
    background: #f4f4fd;
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const CirclePlusIcon = ({ stroke }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 12H16M12 8V16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const BookOpenIcon = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M30 17.5C30 14.8478 28.9464 12.3043 27.0711 10.4289C25.1957 8.55357 22.6522 7.5 20 7.5H5V45H22.5C24.4891 45 26.3968 45.7902 27.8033 47.1967C29.2098 48.6032 30 50.5109 30 52.5M30 17.5V52.5M30 17.5C30 14.8478 31.0536 12.3043 32.9289 10.4289C34.8043 8.55357 37.3478 7.5 40 7.5H55V45H37.5C35.5109 45 33.6032 45.7902 32.1967 47.1967C30.7902 48.6032 30 50.5109 30 52.5"
      stroke="#002E19"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const GroupsEmptyState = ({
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryTitle,
  onQuickGroup,
  quickGroupDisabled = false,
  quickGroupTitle,
  // Tail of the Lesson Groups sentence — the standards flow describes what
  // groups align to differently than the lesson flow.
  lessonGroupsDetail = "align with your daily curriculum and with groups for core, prerequisite, and extension levels.",
  style,
}) => (
  <Container style={style}>
    <Message>
      <BookOpenIcon />
      <Heading>Let&rsquo;s set up your first small groups!</Heading>
      <BulletList>
        <li>
          Create <strong>Lesson Groups</strong> to {lessonGroupsDetail}
        </li>
        <li>
          Create <strong>Quick Group</strong> to instantly generate a standalone group code for targeted practice on a
          specific standard.
        </li>
      </BulletList>
    </Message>
    <Buttons>
      <PrimaryButton onClick={onPrimary} disabled={primaryDisabled} title={primaryTitle}>
        <CirclePlusIcon stroke="#FAF7F2" />
        {primaryLabel}
      </PrimaryButton>
      <SecondaryButton onClick={onQuickGroup} disabled={quickGroupDisabled} title={quickGroupTitle}>
        <CirclePlusIcon stroke="#605DEC" />
        Create Quick Group
      </SecondaryButton>
    </Buttons>
  </Container>
);
